/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */
import React from 'react'
// import localData from '../example.csv'
import * as d3 from 'd3-fetch'
import { hot } from 'react-hot-loader/root'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import { Form } from 'semantic-ui-react'
import styled, { keyframes } from 'styled-components'
import SparkLine from '../sparkline'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import renderHTML from 'react-render-html'
import './styles.scss'

// GET PARAMS FROM URL
const params = new URL(window.location).searchParams

export class AppComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      cleanedData: [],
      isActive: false,
      isFinished: false,
      estimate: 50,
      spread: 0,
      result: 'unklar',
      label: '',
      communitiesCounted: 0,
      status: '',
      ballotTitle: '',
      ballotID: params.get('ballot'),
      projectionLanguage: params.get('lang'),
      standesStimmen: -1,
      staendeOverride: false
    }

    this.fetchData = this.fetchData.bind(this)
  }

  handleChange = (e, { name, value }) => this.setState({ estimate: value })
  handleSpreadChange = (e, { name, value }) => this.setState({ spread: value })
  handleResultChange = (e, { name, value }) => this.setState({ result: value })
  handleStatusChange = (e, { name, value }) => this.setState({ status: value })

  calculateTest(estimate) {
    const styleObj = {
      transform: 'rotate(' + estimate + 'deg)'
      // animation: tester + '2s linear infinite'
    }
    return styleObj
  }

  calculateRotation(estimate) {
    // calculate the needles angle/rotation
    const needleRotation = (estimate / 100) * 180 - 89.5 + 'deg'
    return needleRotation
  }

  calculateSpreadWidth(spread) {
    // calculate the spread indicators width
    const spreadWidth = spread / 2
    return spreadWidth
  }

  calculateSpreadPercentage(spread) {
    let spreadPercentage = Math.round(spread * 10) / 10
    spreadPercentage = spreadPercentage
      .toString()
      .split('.')
      .join(',')
    return spreadPercentage + '%'
  }

  calculateSpreadOpacity(spread) {
    // calculate the opacity of the spread indicator
    let spreadOpacity = 1
    if (spread > 5 && spread < 10) {
      spreadOpacity = 5 / spread
    } else if (spread <= 1) {
      spreadOpacity = 1
    } else if (spread >= 10) {
      spreadOpacity = 0.5
    }
    return spreadOpacity
  }

  calculateSpreadTextPosition(estimate) {
    // calculate the top position of the spread txt
    if (estimate > 50) {
      estimate = 100 - estimate
    }

    let spreadTop = -1.4
    if (estimate <= 50 && estimate > 30) {
      spreadTop = -(4.6 / estimate) - 1.4
    } else if (estimate > 20 && estimate <= 30) {
      spreadTop = -(4.6 / estimate) - 2.8
    } else if (estimate > 5 && estimate <= 20) {
      spreadTop = -(4.6 / estimate) - 4.2
    } else if (estimate >= 2 && estimate <= 5) {
      spreadTop = -(4.6 / estimate) - 4.2
    } else if (estimate >= 1 && estimate < 2) {
      spreadTop = -(4.6 / estimate) - 1.4
    } else if (estimate < 1) {
      spreadTop = -6
    }
    return spreadTop + 'vw'
  }

  calculateWiggle(spread, factor) {
    // calculate roatation degrees for the current spread
    const wiggleRotation = (((180 / 100) * spread) / 2) * factor + 'deg'
    // console.log('wiggleRotation: ', wiggleRotation)
    return wiggleRotation
  }

  calculateResultLabel(estimate) {
    // calculate roatation degrees for the current spread
    let resultClass = 'tie'
    if (estimate > 50) {
      resultClass = 'approved'
    } else if (estimate < 50) {
      resultClass = 'declined'
    } else {
      resultClass = 'tie'
    }
    return resultClass
  }

  calculateResultPercentage(estimate) {
    // calculate roatation degrees for the current spread
    estimate = parseFloat(estimate)
    let resultPercentage = parseFloat(estimate)
    if (estimate < 50) {
      resultPercentage = 100 - estimate
    }
    resultPercentage = Math.round(resultPercentage * 10) / 10
    resultPercentage = resultPercentage
      .toString()
      .split('.')
      .join(',')
    return resultPercentage
  }

  fetchData() {
    const dt = new Date()
    // get meta data from config json
    d3.json(
      'https://tamedia-abstimmungszentrale-hochrechnung.storage.googleapis.com/' +
        this.state.ballotID +
        '.json?' +
        dt.getTime()
    ).then(
      function(configData) {
        // console.log('configData: ', configData)
        const metaData = configData.projections.filter(
          projection =>
            toString(projection.id) === toString(this.state.ballotID)
        )
        if (metaData.length) {
          // console.log('metaData: ', metaData)
          if (
            this.state.projectionLanguage !== '' &&
            this.state.projectionLanguage !== null
          ) {
            const key = 'title-' + this.state.projectionLanguage
            this.setState({ ballotTitle: metaData[0][key] })
          } else if (metaData[0].title !== '' && metaData[0].title !== null) {
            this.setState({ ballotTitle: metaData[0].title })
          }

          //console.log('stände', metaData[0]['no-staendemehr'])
          if(metaData[0]['no-staendemehr']) {
            this.setState({ staendeOverride: true })
          } else {
            this.setState({ staendeOverride: false })
          }
        } else {
          console.log('metaData missing, add to JSON?')
          this.setState({ ballotTitle: 'title not set...' })
        }

        // fetch projection data from csv
        d3.csv(
          'https://tamedia-abstimmungszentrale-hochrechnung.storage.googleapis.com/' +
            this.state.ballotID +
            '.csv?' +
            dt.getTime()
        ).then(
          function(data) {
            console.log('Data: ', data)
            this.parseCSVData(data)
          }.bind(this),
          function(error) {
            this.setState({ isActive: false })
          }.bind(this)
        )
      }.bind(this),
      function(error) {
        console.log('metaData missing, no JSON-file on server!')
      }.bind(this)
    )
  }

  parseCSVData(fetchedData) {
    let cleanedData = fetchedData.filter(row => row.Estimate !== '')
    cleanedData = cleanedData.filter(
      row => row.BfS_number === this.state.ballotID && row.active === '1'
    )
    this.setState({ cleanedData: cleanedData })
    const lastRow = fetchedData[fetchedData.length - 1]
    //console.log('lastRow: ', lastRow)
    const currentActive = !!parseInt(lastRow.active)
    this.setState({ isActive: currentActive }, function() {
      if (this.state.isActive) {
        const currentEstimate = lastRow.Estimate
        this.setState({ estimate: currentEstimate })
        const currentIsFinished = lastRow.Share_counted
        if (currentIsFinished == '1') {
          this.setState({ isFinished: true })
        } else {
          this.setState({ isFinished: false })
        }
        const currentSpread = lastRow.upper_bound - lastRow.lower_bound
        this.setState({ spread: currentSpread })
        let currentResult = 'not set'
        this.state.projectionLanguage === 'fr' && lastRow.result_fr
          ? (currentResult = lastRow.result_fr)
          : this.state.projectionLanguage === 'it' && lastRow.result_it
          ? (currentResult = lastRow.result_it)
          : (currentResult = lastRow.result)
        this.setState({ result: currentResult })
        let currentLabel = 'not set'
        const currentCounted = Math.round(lastRow.Share_counted * 100)
        this.setState({ communitiesCounted: currentCounted })
        this.state.projectionLanguage === 'fr' && lastRow.label_fr
          ? (currentLabel = lastRow.label_fr)
          : this.state.projectionLanguage === 'it' && lastRow.label_it
          ? (currentLabel = lastRow.label_it)
          : (currentLabel = lastRow.label)
        const currentStandesStimmen = +lastRow.Standesstimmen
        this.setState({ standesStimmen: currentStandesStimmen })
        //override result by Staendemehr?
        if(currentStandesStimmen <= 11.5 && currentEstimate > 50 && currentCounted === 100 && currentStandesStimmen != -1) {
          this.setState({ staendeOverride: true })
          this.state.projectionLanguage === 'fr'
            ? currentLabel = '<span className="declined">Échoue, faute de majorité des cantons<span>'
              : this.state.projectionLanguage === 'it'
              ?  currentLabel = '<span className="declined">Respinto per mancata maggioranza dei cantoni<span>'
                : currentLabel = '<span className="declined">Am Ständemehr gescheitert<span>'
        } else {
          this.state.staendeOverride
          ? this.state.projectionLanguage === 'fr'
            ? currentLabel = '<span className="declined">Échoue, faute de majorité des cantons<span>'
            : this.state.projectionLanguage === 'it'
              ?  currentLabel = '<span className="declined">Respinto per mancata maggioranza dei cantoni<span>'
                : currentLabel = '<span className="declined">Am Ständemehr gescheitert<span>'
          : currentLabel = currentLabel
        }
        this.setState({ label: currentLabel })
        const currentStatus = lastRow.Time.substring(0, lastRow.Time.length - 3)
        this.setState({ status: currentStatus })
      } else {
        this.setState({ estimate: 50 })
      }
    })
  }

  startCSVWatcher() {
    //console.log('start CSV Watcher')
    setInterval(() => {
      this.fetchData()
      // console.log('csvWatcher')
    }, 30000)
  }

  componentDidMount() {
    this.fetchData()
    this.startCSVWatcher()
  }

  render() {
    // Create the keyframes
    let wiggler = keyframes`
        100% {
          transform: rotate(0deg);
        }
      `
    if (this.state.isActive && !this.state.isFinished) {
      wiggler = keyframes`
        2% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        5% {
          transform: rotate(${this.state.spread * Math.random()}deg);
        }
        7% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        9% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        11% {
          transform: rotate(${this.state.spread * Math.random()}deg);
        }
        17% {
          transform: rotate(${this.state.spread * Math.random()}deg);
        }
        19% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        22% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        24% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        26% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        29% {
          transform: rotate(${this.state.spread * 0.66}deg);
        }
        32% {
          transform: rotate(${this.state.spread * Math.random()}deg);
        }
        35% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        39% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        45% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        55% {
          transform: rotate(${this.state.spread * Math.random()}deg);
        }
        62% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        68% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        70% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        72% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        75% {
          transform: rotate(-${this.state.spread * 0.66}deg);
        }
        78% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        83% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        86% {
          transform: rotate(${this.state.spread * 0}deg);
        }
        90% {
          transform: rotate(-${this.state.spread * Math.random()}deg);
        }
        100% {
          transform: rotate(${this.state.spread * 0}deg);
        }
      `
    }

    const Wiggle = styled.div`
      animation: ${wiggler} 3.5s linear infinite;
      width: 100%;
      position: absolute;
      transform: rotate(0deg);
      transform-origin: 51.4% 93.8%;
      display: table;
    `
    return (
      <div className='index'>
        <ThemeSwitch />
        <h2>{this.state.ballotTitle}</h2>
        <div className='gauge-wrapper'>
          <div
            className={
              this.state.isFinished
                ? 'gauge active finished'
                : this.state.isActive
                ? 'gauge active'
                : 'gauge inactive'
            }
            style={{
              position: 'relative',
              width: '100%'
            }}
          >
            <img
              src={require('./images/aspect-ratio.png')}
              style={{
                opacity: '0.01%',
                width: '100%',
                height: 'auto',
                zIndex: '-1',
                position: 'relative'
              }}
            />
            <div
              className='content'
              style={{
                position: 'absolute',
                width: '100%',
                top: '0',
                left: '0'
              }}
            >
              {/*
              <img
                src={require('./images/vorlage.svg')}
                style={{ width: '100%', position: 'absolute' }}
              />
            */}
              <img
                src={require('./images/skala.svg')}
                style={{ width: '100%', position: 'absolute' }}
              />
              <div className='placing-helper'>
                <div
                  className='spread-wrapper'
                  style={{
                    transform:
                      'rotate(' +
                      this.calculateRotation(this.state.estimate) +
                      ')'
                  }}
                >
                  <div
                    className='spread-txt'
                    style={{
                      transform:
                        'rotate(' +
                        ((this.state.estimate - 50) / 50) * -90 +
                        'deg)',
                      top: this.calculateSpreadTextPosition(this.state.estimate)
                    }}
                  >
                    <nobr>
                      &plusmn;{' '}
                      {this.calculateSpreadPercentage(this.state.spread / 2)}
                    </nobr>
                  </div>
                  <div
                    className='spread-scaler'
                    style={{
                      transform:
                        'scaleX(' +
                        this.calculateSpreadWidth(this.state.spread) +
                        ')'
                      // opacity: this.calculateSpreadOpacity(this.state.spread)
                    }}
                  >
                    <img
                      src={require('./images/spread.svg')}
                      className='spread'
                      style={{
                        width: '100%',
                        display: 'table-row',
                        opacity: 0
                      }}
                    />
                  </div>
                </div>

                <Wiggle className='wiggler'>
                  <div
                    className='needle-wrapper'
                    style={{
                      transform:
                        'rotate(' +
                        this.calculateRotation(this.state.estimate) +
                        ')',
                      display: 'table',
                      overflow: 'auto'
                    }}
                  >
                    <img
                      src={require('./images/needle.svg')}
                      className='needle'
                      style={{ width: '100%', display: 'table-row' }}
                    />
                  </div>
                </Wiggle>
              </div>
            </div>
          </div>

          <div
            className={
              this.state.isFinished
                ? 'projection active finished'
                : this.state.isActive
                ? 'projection active'
                : 'projection inactive'
            }
          >
            <div
              className={
                this.state.isActive
                  ? this.state.staendeOverride
                    ? 'result override ' + this.calculateResultLabel(this.state.estimate)
                    : 'result ' + this.calculateResultLabel(this.state.estimate)
                  : 'result tie'
              }
            >
              {this.state.isActive && (
                <span
                  className={
                    this.state.staendeOverride
                      ? 'estimate override ' + this.calculateResultLabel(this.state.estimate)
                      : 'estimate ' + this.calculateResultLabel(this.state.estimate)
                  }
                >
                  {this.calculateResultPercentage(this.state.estimate) + '% '}
                </span>
              )}
              {this.state.isActive
                ? this.state.result
                : this.state.projectionLanguage === 'fr'
                ? 'Pas encore de projection'
                : this.state.projectionLanguage === 'it'
                ? 'Ancora nessuna proiezione'
                : 'Noch keine Hochrechnung'}
              <span className={this.state.staendeOverride ? 'check override' : 'check'}>
                {this.state.isFinished && (
                  <img src={require('./images/check.svg')} />
                )}
              </span>
            </div>
            {this.state.isActive && (
              <div className={this.state.staendeOverride ? 'label override' : 'label'}>{renderHTML(this.state.label)}</div>
            )}
            {this.state.isActive && this.state.standesStimmen >= 0 && (
              <div className='staende'>
                {this.state.projectionLanguage === 'fr'
                  ? 'Suffrage des Cantons: ' 
                  : this.state.projectionLanguage === 'it'
                  ? 'Voti dei cantoni: '
                  : 'Standesstimmen: '} 
                <div className={this.state.standesStimmen > 11.5 ? 'ui horizontal tiny label' : 'ui declined horizontal tiny label'}>
                {this.state.projectionLanguage === 'fr'
                    ? 'Non' 
                    : this.state.projectionLanguage === 'it'
                    ? 'No'
                    : 'Nein'}
                  </div><span className='count'>{23 - this.state.standesStimmen}</span>
                <span className='separator'>|</span>
                <div className={this.state.standesStimmen > 11.5 ? 'ui approved horizontal tiny label' : 'ui horizontal tiny label'}>
                  {this.state.projectionLanguage === 'fr'
                    ? 'Oui' 
                    : this.state.projectionLanguage === 'it'
                    ? 'Si'
                    : 'Ja'}
                </div>
                <span className='count'>{this.state.standesStimmen}</span>
                
              </div>
            )}
            {this.state.isActive && (
              <div className='status'>
                {this.state.communitiesCounted}%{' '}
                {this.state.projectionLanguage === 'fr'
                  ? 'des communes'
                  : this.state.projectionLanguage === 'it'
                  ? 'dei Comuni'
                  : 'der Gemeinden'}
                ,{' '}
                {this.state.projectionLanguage === 'fr'
                  ? this.state.status.replace(':', 'h')
                  : this.state.projectionLanguage === 'it'
                  ? 'Ore ' + this.state.status
                  : this.state.status + ' Uhr'}
              </div>
            )}
          </div>
        </div>
        {this.state.isActive && (
          <div className='spark-wrapper' style={{ paddingRight: '2em' }}>
            <SparkLine
              csvData={this.state.cleanedData}
              ballotID={this.state.ballotID}
              ballotTitle={this.state.ballotTitle}
              lineColors={{
                approved: '#509133',
                declined: '#c51b7d',
                tie: '#777777'
              }}
              projectionLanguage={this.state.projectionLanguage}
            />
          </div>
        )}
        <div className='credits'>
          {this.state.projectionLanguage === 'fr'
            ? 'Source'
            : this.state.projectionLanguage === 'it'
            ? 'Fonte'
            : 'Quelle'}
          :{' '}
          <a href='https://leewas.ch/' target='_PARENT'>
            LeeWas
          </a>
          ,{' '}
          {this.state.projectionLanguage === 'fr'
            ? 'données brutes'
            : this.state.projectionLanguage === 'it'
            ? 'dati grezzi'
            : 'Rohdaten'}
          :{' '}
          <a
            href={
              this.state.projectionLanguage === 'fr'
                ? 'https://opendata.swiss/fr/organization/bundesamt-fur-statistik-bfs'
                : this.state.projectionLanguage === 'it'
                ? 'https://opendata.swiss/it/organization/bundesamt-fur-statistik-bfs'
                : 'https://opendata.swiss/de/organization/bundesamt-fur-statistik-bfs'
            }
            target='_PARENT'
          >
            {this.state.projectionLanguage === 'fr'
              ? 'OFS'
              : this.state.projectionLanguage === 'it'
              ? 'UST'
              : 'BfS'}
          </a>
        </div>

        {/*  FOR DESIGN DECISIONS
        <div style={{ textAlign: 'center', marginTop: '1.5em' }}>
          
          <Form.Input
            placeholder='Wahrscheinlichkeit'
            name='probabilityValue'
            onChange={this.handleChange}
            style={{
              zIndex: '2',
              position: 'absolute',
              fontSize: '0.8em',
              left: '0',
              right: '0'
            }}
          />
          <br />
          <Form.Input
            placeholder='Unsicherheit'
            name='spreadValue'
            onChange={this.handleSpreadChange}
            style={{
              zIndex: '3',
              fontSize: '0.8em',
              left: '0',
              right: '0',
              position: 'absolute',
              marginTop: '0.5em'
            }}
          />
          <br />
          <Form.Input
            placeholder='Resultattext'
            name='resultText'
            onChange={this.handleResultChange}
            style={{
              fontSize: '0.8em',
              left: '0',
              right: '0',
              zIndex: '4',
              position: 'absolute',
              marginTop: '1em'
            }}
          />
          <br />
          <Form.Input
            placeholder='Status-Text'
            name='statusText'
            onChange={this.handleStatusChange}
            style={{
              fontSize: '0.8em',
              left: '0',
              right: '0',
              zIndex: '4',
              position: 'absolute',
              marginTop: '1.5em'
            }}
          />
          <br />
          <a
            href='#'
            onClick={this.fetchData}
            style={{
              zIndex: '5',
              position: 'absolute',
              marginTop: '1.5em',
              left: '0',
              right: '0',
              textAlign: 'center'
            }}
          >
            Reload CSV
          </a>
         
        </div>
         */}
      </div>
    )
  }
}

export default hot('AppComponent')
